// React
import React, { Fragment, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Libraries
import _debounce from 'lodash/debounce'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleInfo as farCircleInfo,
  faImage as farImage,
  faMinus as farMinus,
  faPlus as farPlus
} from '@fortawesome/pro-regular-svg-icons'
import { faTimes as fasTimes } from '@fortawesome/pro-solid-svg-icons'

// Components
import BundleContents from './BundleContents'
import BLine from 'shop-components/components/MiniCart/styles/Line'
import Tooltip from 'ui/components/Tooltip'
import FocalImage from 'ui/components/FocalImage'

// Shared
import Format from 'shared/utils/format'
import cartActions from 'store/v2/redux/actions/cart'
import { withTranslation } from 'shared/utils/withTranslation'
import { triggerUserFrameworkBookEvent } from 'shared/utils/userFrameworkEvents'

export const Line = ({ line, useAvailability, hasDates, t, currency, showPrices, hideProductAvailabilityQuantities, updateLineQuantity }) => {
  const [quantity, setQuantity] = useState(line.quantity || 1)
  const [deleting, setDeleting] = useState(false)
  const available = line.available_quantity >= quantity

  useEffect(() => {
    setQuantity(line.quantity)
  }, [line])

  const debouncedUpdateLineQuantity = useCallback(
    window.testMode ? updateLineQuantity : _debounce(updateLineQuantity, 1500),
    []
  )

  const handleRemoveLine = () => {
    setDeleting(true)
    updateLineQuantity(0)
  }

  const handleIncreaseQuantity = () => {
    const newQuantity = quantity + 1

    setQuantity(newQuantity)
    debouncedUpdateLineQuantity(newQuantity)
  }

  const handleDecreaseQuantity = () => {
    const newQuantity = quantity - 1

    if (newQuantity <= 0) {
      handleRemoveLine()
    } else {
      setQuantity(newQuantity)
      debouncedUpdateLineQuantity(newQuantity)
    }
  }

  if (line.line_type === 'delivery_rate') return null

  return (
    <BLine key={line.title}>
      <BLine.Details>
        <BLine.Image>
          {line?.photo_url && (
            <FocalImage url={line.photo_url} />
          )}
          {!line?.photo_url && (
            <span>
              <FontAwesomeIcon icon={farImage} size="lg" />
            </span>
          )}
        </BLine.Image>
        <div>
          <BLine.DetailContainer center>
            <div>
              <BLine.ProductName large={!useAvailability}>{line.title}</BLine.ProductName>
              {hasDates && useAvailability && (
                <BLine.StatusIndicator available={available}>
                  {!hideProductAvailabilityQuantities && (
                    <Fragment>
                      <span>{line.available_quantity}</span> {t('user.store.available')}
                    </Fragment>
                  )}
                  {hideProductAvailabilityQuantities && (
                    <Fragment>
                      {t(available ? 'user.store.available' : 'user.store.unavailable')}
                    </Fragment>
                  )}
                </BLine.StatusIndicator>
              )}
            </div>
            <BLine.Remove data-tid="Remove product" onClick={deleting ? null : handleRemoveLine}>
              <FontAwesomeIcon icon={fasTimes} />
            </BLine.Remove>
          </BLine.DetailContainer>
          <BLine.DetailContainer>
            <BLine.Quantity>
              <BLine.QuantityButton onClick={deleting ? null : handleDecreaseQuantity}>
                <FontAwesomeIcon icon={farMinus} />
              </BLine.QuantityButton>
              <span>{quantity}</span>
              <BLine.QuantityButton onClick={handleIncreaseQuantity}>
                <FontAwesomeIcon icon={farPlus} />
              </BLine.QuantityButton>
            </BLine.Quantity>
            {showPrices && line.display_price_in_cents > 0 && (
              <BLine.Price>
                {showPrices && !line.discountable && (
                  <Tooltip direction="bottom" content={t('user.store.not_discountable_hint')}>
                    <FontAwesomeIcon icon={farCircleInfo} />
                  </Tooltip>
                )}
                <p>{Format.money(line.display_price_in_cents, currency)}</p>
              </BLine.Price>
            )}
          </BLine.DetailContainer>
        </div>
      </BLine.Details>
      {line.product_type === 'bundle' && <BundleContents line={line} />}
    </BLine>
  )
}

Line.propTypes = {
  line: PropTypes.object,
  useAvailability: PropTypes.bool,
  hasDates: PropTypes.bool,
  currency: PropTypes.object,
  showPrices: PropTypes.bool,
  hideProductAvailabilityQuantities: PropTypes.bool,
  updateLineQuantity: PropTypes.func,
  t: PropTypes.func
}

Line.displayName = 'Line'

const mapStateToProps = (state, props) => {
  return {
    currency: state.client.currency.toJS(),
    useAvailability: state.settings.get('store.use_availability') && props.line.tracking_type !== 'none',
    showPrices: state.settings.get('store.use_prices'),
    hideProductAvailabilityQuantities: state.settings.get('store.hide_product_availability_quantities')
  }
}

const mapDispatchToProps = (_, props) => {
  return {
    updateLineQuantity: (quantity) => {
      if (quantity < 0) {
        return
      }

      return cartActions
        .book({
          item_id: props.line.item_id,
          line_id: props.line.id,
          quantity,
          type: 'set'
        })
        .then(() => {
          const quantityChange = quantity - props.line.quantity

          triggerUserFrameworkBookEvent(props.line.item_id, quantityChange)
        })
    }
  }
}

export default compose(withTranslation('user'), reduxConnect(mapStateToProps, mapDispatchToProps))(Line)
